import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plug } from "lucide-react";

import { Button } from "components/common/buttons/Button";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/common/TooltipV3";
import { ENVS } from "config";
import { useClaudeConnector } from "hooks/workspace/useClaudeConnector";
import { useMcpConnections } from "hooks/workspace/useMcpConnections";
import { AiAppsConnectDialog } from "pages/settings/workspace/connections/components";
import type { HeaderAction } from "./ReferralButton";

export const CONNECT_AI_APPS_LABEL = "Connect AI apps";

/**
 * Setup shortcut that opens the add buttons in a dialog (or the connections
 * page when no OAuth URL is configured). It is offered only until the
 * signed-in user has connected any AI app, since by then there is nothing
 * left to set up. An empty workspace id offers nothing. Both statuses must be
 * known first: a pending or failed request, including a 403 on the Claude
 * connector, does not prove that nothing is connected, so it offers nothing.
 */
export function useConnectAiAppsAction(workspaceId: string): HeaderAction {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const { statusQuery: claudeStatusQuery } = useClaudeConnector(workspaceId);
  // Poll only while the dialog is open: connecting finishes in another app.
  const { connectionsQuery } = useMcpConnections(workspaceId, "mine", { poll: isOpen });
  const connectionsPath = `/workspaces/${workspaceId}/settings/connections`;

  const isConnected =
    Boolean(claudeStatusQuery.data?.connected) || (connectionsQuery.data ?? []).length > 0;
  // Wait for both statuses so the action never flashes for people already connected.
  const statusUnknown = connectionsQuery.data === undefined || claudeStatusQuery.data === undefined;
  const available =
    Boolean(workspaceId) && !connectionsQuery.isLoading && !statusUnknown && !isConnected;

  return {
    available,
    open: () => (ENVS.mcpOAuthUrl ? setIsOpen(true) : navigate(connectionsPath)),
    dialog:
      workspaceId && ENVS.mcpOAuthUrl ? (
        <AiAppsConnectDialog
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          url={ENVS.mcpOAuthUrl}
          connectionsPath={connectionsPath}
        />
      ) : null,
  };
}

/** The plug icon button in the header. */
export const ConnectAiAppsIconButton = ({ onOpen }: { onOpen: () => void }) => (
  <Tooltip delayDuration={400}>
    <TooltipTrigger asChild>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label={CONNECT_AI_APPS_LABEL}
        onClick={onOpen}
        className="inline-flex h-8 w-8 min-w-0 shrink-0 items-center justify-center rounded-md p-0 text-foreground hover:bg-muted"
      >
        <Plug className="h-4 w-4" aria-hidden />
      </Button>
    </TooltipTrigger>
    <TooltipContent side="bottom" align="end" sideOffset={6} className="max-w-xs">
      Connect AI apps: run breakdowns, schedules, and budgets from Claude, ChatGPT, or Cursor.
    </TooltipContent>
  </Tooltip>
);

/** The plug icon button and its dialog. */
export const ConnectAiAppsButton = ({ workspaceId }: { workspaceId: string }) => {
  const connect = useConnectAiAppsAction(workspaceId);
  return (
    <>
      {connect.available ? <ConnectAiAppsIconButton onOpen={connect.open} /> : null}
      {connect.dialog}
    </>
  );
};
